import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Twitter, Linkedin, Mail, Globe, BookOpen, Clock } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import BlogCard from "../components/BlogCard";
import { getAuthorByName, getPostsByAuthor } from "../data/blogPosts";

import { useTranslation } from 'react-i18next';
export default function AuthorPage() {
  const { t } = useTranslation();
  const { name } = useParams();
  const decodedName = decodeURIComponent(name);
  const author = getAuthorByName(decodedName);
  const posts = getPostsByAuthor(decodedName);

  if (!author) {
    return (
      <div className="min-h-screen bg-[#09090B] text-white">
        <Header />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 text-center">
          <h1 className="font-display text-4xl uppercase mb-4">{t('author_page.author_not_found')}</h1>
          <p className="text-zinc-400 mb-8">{t('author_page.the_author_youre_looking_for_doesnt_exist')}</p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-6 py-3 font-bold uppercase tracking-wider text-xs transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  // Calculate total reading time
  const totalReadMinutes = posts.reduce((acc, post) => {
    const match = post.readTime ? post.readTime.match(/(\d+)/) : null;
    return acc + (match ? parseInt(match[1], 10) : 0);
  }, 0);
  const totalReadHours = Math.floor(totalReadMinutes / 60);
  const remainingMinutes = totalReadMinutes % 60;
  const totalReadLabel = totalReadHours > 0
    ? `${totalReadHours}h ${remainingMinutes}min read`
    : `${totalReadMinutes}min read`;

  // Build sameAs array for Person schema
  const sameAs = [
    author.social?.twitter,
    author.social?.linkedin,
    author.social?.website,
  ].filter(Boolean);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: author.name,
    description: author.bio,
    url: `https://maxemployee.com/blog/author/${encodeURIComponent(author.name)}`,
    image: author.avatar,
    sameAs,
    knowsAbout: [...new Set(posts.flatMap((p) => p.tags || []))],
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <SEOHead
        title={`${author.name} | Max Employee Blog`}
        description={author.bio || `Articles by ${author.name} on the Max Employee blog.`}
        url={`/blog/author/${encodeURIComponent(author.name)}`}
        structuredData={structuredData}
      />
      <Header />

      <main id="main-content">
        {/* Author Hero */}
        <section className="border-b border-zinc-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 pt-8 sm:pt-12 pb-10 sm:pb-16">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-zinc-400 hover:text-amber-500 font-mono text-xs uppercase tracking-[0.2em] mb-8 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Back to Blog
            </Link>

            <div className="flex flex-col sm:flex-row items-start gap-5 sm:gap-6">
              {/* Avatar */}
              <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-zinc-800 border-2 border-amber-500/30 flex items-center justify-center shrink-0 overflow-hidden shadow-lg shadow-amber-500/5">
                {author.avatar ? (
                  <img
                    src={author.avatar}
                    alt={author.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <span className="font-display text-3xl sm:text-4xl uppercase text-amber-500">
                    {author.name.charAt(0)}
                  </span>
                )}
              </div>

              <div className="flex-1">
                <h1 className="font-display text-2xl sm:text-3xl md:text-4xl uppercase text-white mb-2">
                  {author.name}
                </h1>
                {author.role && (
                  <span className="inline-block font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 bg-amber-500/10 px-2 py-1 mb-4">
                    {author.role}
                  </span>
                )}
                {author.bio && (
                  <p className="text-zinc-400 leading-relaxed mt-3 max-w-2xl text-sm sm:text-base">
                    {author.bio}
                  </p>
                )}

                {/* Stats bar */}
                <div className="flex items-center gap-4 sm:gap-6 mt-5 pt-5 border-t border-zinc-800/60">
                  <div className="flex items-center gap-1.5 text-zinc-400">
                    <BookOpen className="w-4 h-4 text-amber-500" />
                    <span className="font-mono text-xs uppercase tracking-wider">
                      {posts.length} article{posts.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-zinc-400">
                    <Clock className="w-4 h-4 text-amber-500" />
                    <span className="font-mono text-xs uppercase tracking-wider">
                      {totalReadLabel}
                    </span>
                  </div>
                </div>

                {/* Social links */}
                <div className="flex items-center gap-4 mt-5">
                  {author.social?.twitter && (
                    <a
                      href={author.social.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-zinc-500 hover:text-amber-500 transition-colors"
                      aria-label={`${author.name} on Twitter`}
                    >
                      <Twitter className="w-4 h-4" />
                      <span className="font-mono text-[10px] uppercase tracking-wider">{t('referral.twitter')}</span>
                    </a>
                  )}
                  {author.social?.linkedin && (
                    <a
                      href={author.social.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-zinc-500 hover:text-amber-500 transition-colors"
                      aria-label={`${author.name} on LinkedIn`}
                    >
                      <Linkedin className="w-4 h-4" />
                      <span className="font-mono text-[10px] uppercase tracking-wider">{t('referral.linkedin')}</span>
                    </a>
                  )}
                  {author.social?.website && (
                    <a
                      href={author.social.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-zinc-500 hover:text-amber-500 transition-colors"
                      aria-label={`${author.name} website`}
                    >
                      <Globe className="w-4 h-4" />
                      <span className="font-mono text-[10px] uppercase tracking-wider">{t('author_page.website')}</span>
                    </a>
                  )}
                  <a
                    href="mailto:hello@maxemployee.com"
                    className="flex items-center gap-1.5 text-zinc-500 hover:text-amber-500 transition-colors"
                    aria-label={t('author_page.email')}
                  >
                    <Mail className="w-4 h-4" />
                    <span className="font-mono text-[10px] uppercase tracking-wider">{t('author_page.email')}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Author's Posts */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-16">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-4">
            // {posts.length} ARTICLE{posts.length !== 1 ? "S" : ""} BY {author.name.toUpperCase()}
          </div>
          <h2 className="font-display text-xl sm:text-2xl uppercase text-white mb-6 sm:mb-10">{t('author_page.published_articles')}</h2>

          {posts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {posts.map((post) => (
                <BlogCard key={post.slug} post={post} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12" role="status">
              <p className="text-zinc-400">{t('author_page.no_articles_found_for_this_author')}</p>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}
